const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const SESSION_KEY = "tulocalmx_auth_session";
const REFRESH_MARGIN_SECONDS = 60;

const authHeaders = (token) => ({
  apikey: supabaseAnonKey,
  Authorization: `Bearer ${token || supabaseAnonKey}`,
  "Content-Type": "application/json",
});

const authFetch = async (path, options = {}) => {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase Auth no esta configurado");
  }

  const response = await fetch(`${supabaseUrl}/auth/v1/${path}`, {
    ...options,
    headers: {
      ...authHeaders(options.accessToken),
      ...(options.headers || {}),
    },
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new Error(data?.msg || data?.error_description || data?.error || "No se pudo completar la solicitud");
  }

  return data;
};

export const saveAuthSession = (session, remember = true) => {
  if (!session?.access_token) return null;
  const storage = remember ? localStorage : sessionStorage;
  const otherStorage = remember ? sessionStorage : localStorage;

  storage.setItem(SESSION_KEY, JSON.stringify(session));
  otherStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event("tulocalmx-auth-change"));
  return session;
};

export const getAuthSession = () => {
  try {
    const raw =
      localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const isPersistentSession = () => Boolean(localStorage.getItem(SESSION_KEY));

const shouldRefreshSession = (session) => {
  if (!session?.refresh_token) return false;
  if (!session.expires_at) return true;

  const expiresAt = Number(session.expires_at);
  if (!Number.isFinite(expiresAt)) return true;

  return expiresAt - Math.floor(Date.now() / 1000) <= REFRESH_MARGIN_SECONDS;
};

export const refreshAuthSession = async (session = getAuthSession()) => {
  if (!session?.refresh_token) return session || null;

  const data = await authFetch("token?grant_type=refresh_token", {
    method: "POST",
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  });

  return saveAuthSession(
    {
      ...session,
      ...data,
      user: data.user || session.user,
    },
    isPersistentSession()
  );
};

export const getValidAuthSession = async () => {
  const session = getAuthSession();
  if (!session?.access_token) return null;
  if (!shouldRefreshSession(session)) return session;

  try {
    return await refreshAuthSession(session);
  } catch (error) {
    clearAuthSession();
    return null;
  }
};

export const clearAuthSession = () => {
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event("tulocalmx-auth-change"));
};

export const getCurrentUser = () => getAuthSession()?.user || null;

export const signUpWithPassword = async ({ email, password, metadata, redirectTo }) =>
  authFetch(
    `signup?redirect_to=${encodeURIComponent(
      redirectTo || `${window.location.origin}/iniciar-sesion?confirmed=1`
    )}`,
    {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      data: metadata,
    }),
    }
  );

export const signInWithPassword = async ({ email, password, remember = true }) => {
  const data = await authFetch("token?grant_type=password", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  return saveAuthSession(data, remember);
};

export const signInWithGoogle = ({ next = "/", mode = "general" } = {}) => {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase Auth no esta configurado");
  }

  const redirectTo = `${window.location.origin}/completar-perfil?next=${encodeURIComponent(next)}&mode=${encodeURIComponent(mode)}`;
  window.location.href = `${supabaseUrl}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirectTo)}`;
};

export const updateUserMetadata = async ({ accessToken, metadata }) => {
  const user = await authFetch("user", {
    method: "PUT",
    accessToken,
    body: JSON.stringify({ data: metadata }),
  });

  const currentSession = getAuthSession();
  if (currentSession?.access_token === accessToken) {
    saveAuthSession(
      {
        ...currentSession,
        user,
      },
      Boolean(localStorage.getItem(SESSION_KEY))
    );
  }

  return user;
};

export const updateUserEmail = async ({ accessToken, email, redirectTo }) => {
  const user = await authFetch(
    `user?redirect_to=${encodeURIComponent(
      redirectTo || `${window.location.origin}/editar-datos?email_confirmed=1`
    )}`,
    {
      method: "PUT",
      accessToken,
      body: JSON.stringify({ email }),
    }
  );

  const currentSession = getAuthSession();
  if (currentSession?.access_token === accessToken) {
    saveAuthSession(
      {
        ...currentSession,
        user,
      },
      Boolean(localStorage.getItem(SESSION_KEY))
    );
  }

  return user;
};

export const sendPasswordRecovery = async (email) =>
  authFetch(
    `recover?redirect_to=${encodeURIComponent(
      `${window.location.origin}/cambiar-contrasena`
    )}`,
    {
    method: "POST",
    body: JSON.stringify({ email }),
    }
  );

export const updatePassword = async ({ accessToken, password }) =>
  authFetch("user", {
    method: "PUT",
    accessToken,
    body: JSON.stringify({ password }),
  });

export const getUserByAccessToken = async (accessToken) =>
  authFetch("user", {
    method: "GET",
    accessToken,
  });

export const extractSessionFromUrl = () => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const tokenType = params.get("token_type") || "bearer";

  if (!accessToken) return null;

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: tokenType,
    user: getCurrentUser(),
  };
};

export const saveSessionFromUrl = async (remember = true) => {
  const session = extractSessionFromUrl();
  if (!session?.access_token) return null;

  const user = await getUserByAccessToken(session.access_token);
  const fullSession = {
    ...session,
    user,
  };

  saveAuthSession(fullSession, remember);
  window.history.replaceState(null, "", window.location.pathname);
  return fullSession;
};
